import React from 'react';
import { pageStyles, getDefaultGradient } from '../../config/theme';
import { useAdventureStyle } from '../../hooks';

interface AuthPageLayoutProps {
  children: React.ReactNode;
  title: string | React.ReactNode;
  subtitle?: string;
  useAdventureGradient?: boolean; // Whether to use adventure-based gradient or default
}

export const AuthPageLayout: React.FC<AuthPageLayoutProps> = ({
  children,
  title,
  subtitle,
  useAdventureGradient = false,
}) => {
  // Get the resolved gradient from the cascade if enabled
  const adventureStyle = useAdventureStyle();
  
  // Choose background: adventure gradient if enabled, otherwise default
  const background = useAdventureGradient 
    ? adventureStyle.backgroundGradient 
    : getDefaultGradient();
  
  return (
    <div style={{
      ...pageStyles.gradientBackground,
      background, // Override with dynamic gradient
      transition: 'background 0.5s ease-in-out', // Smooth transition between gradients
    }}>
      <div style={pageStyles.container}>
        <div style={pageStyles.header}>
          <h1 style={pageStyles.title}>{title}</h1>
          {subtitle && <p style={pageStyles.subtitle}>{subtitle}</p>}
        </div>
        
        <div style={pageStyles.formContainer}>
          {children}
        </div>
      </div>
    </div>
  );
};

interface AuthFormProps {
  children: React.ReactNode;
  onSubmit: (e: React.FormEvent) => void;
}

export const AuthForm: React.FC<AuthFormProps> = ({ children, onSubmit }) => {
  return (
    <form style={pageStyles.form} onSubmit={onSubmit}>
      {children}
    </form>
  );
};

interface AuthSuccessLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle: string;
  useAdventureGradient?: boolean; // Whether to use adventure-based gradient or default
}

export const AuthSuccessLayout: React.FC<AuthSuccessLayoutProps> = ({
  children,
  title,
  subtitle,
  useAdventureGradient = false,
}) => {
  // Get the resolved gradient from the cascade if enabled
  const adventureStyle = useAdventureStyle();
  
  // Choose background: adventure gradient if enabled, otherwise default
  const background = useAdventureGradient 
    ? adventureStyle.backgroundGradient 
    : getDefaultGradient();
  
  return (
    <div style={{
      ...pageStyles.gradientBackground,
      background, // Override with dynamic gradient
      transition: 'background 0.5s ease-in-out', // Smooth transition between gradients
    }}>
      <div style={pageStyles.container}>
        <div style={pageStyles.success.container}>
          <div style={pageStyles.success.icon}>
            <svg style={pageStyles.success.iconSvg} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 style={pageStyles.title}>{title}</h1>
          <p style={pageStyles.subtitle}>{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
};
